/**
 * Shared types for the action layer. See `MACHINE.md` for the transition
 * matrix and `apps/s-process/docs/architecture/ACTION_PATTERN.md` for why the
 * design looks like this.
 */

/**
 * The status of one attempt. `idle` is deliberately absent: the absence of an
 * attempt IS idle, so "never started" and "finished and acknowledged" cannot
 * drift apart.
 */
export type ActionStatus =
  | "sent"
  | "applying"
  | "succeeded"
  | "failed"
  | "canceled"
  | "unknown";

/** Statuses where work is still outstanding and the control must read busy. */
export const PENDING_STATUSES: ReadonlySet<ActionStatus> =
  new Set<ActionStatus>(["sent", "applying"]);

/** Statuses that will not change on their own. */
export const TERMINAL_STATUSES: ReadonlySet<ActionStatus> =
  new Set<ActionStatus>(["succeeded", "failed", "canceled", "unknown"]);

export function isPendingStatus(status: ActionStatus): boolean {
  return PENDING_STATUSES.has(status);
}

/**
 * What the server gave us to fence against. `runId` is the preferred token;
 * `requestedAt` is the fallback the async-workload rules allow when a workload
 * has no explicit run identity.
 */
export interface ActionIdentity {
  runId?: string;
  requestedAt?: string;
}

export interface Attempt {
  /** Monotonic per key. Every server-derived event must carry a matching id. */
  attemptId: number;
  status: ActionStatus;
  /** Bound when the server accepts; absent while `sent`. */
  identity: ActionIdentity | null;
  /**
   * Set when the control plane reported success but the read model hasn't
   * confirmed it yet. Success may not resolve while this is true — that gap is
   * the flash this whole layer exists to remove.
   */
  awaitingDisplay: boolean;
  /** Presentation only. Never affects which transitions are legal. */
  slow: boolean;
  error: string | null;
  startedAt: number;
}

/** How the run ended, as reported by the control plane. */
export type ControlTerminal = "SUCCEEDED" | "FAILED" | "CANCELED";

export type ActionEvent =
  /**
   * `attemptId` is supplied by the store, which keeps a per-key counter that
   * survives eviction. Without it the id restarts at 1 after a viewer switch or
   * release, and a late callback from the ORIGINAL attempt 1 passes the fence
   * and settles its replacement. Omitted only in tests, where it falls back to
   * incrementing the current attempt.
   */
  | { type: "TRIGGER"; at: number; attemptId?: number }
  | { type: "ACCEPTED"; attemptId: number; identity: ActionIdentity }
  /** Server answered but queued nothing — the result was already current. */
  | { type: "ACCEPTED_SETTLED"; attemptId: number }
  | { type: "REJECTED"; attemptId: number; error: string }
  /** Timeout / connection lost. NOT a failure — see §4.9. */
  | { type: "INDETERMINATE"; attemptId: number; error: string }
  | {
      type: "CONTROL_TERMINAL";
      attemptId: number;
      terminal: ControlTerminal;
      error?: string;
    }
  | { type: "DISPLAY_CONFIRMED"; attemptId: number }
  | { type: "EVIDENCE_APPLIED"; attemptId: number }
  | { type: "SUPERSEDED" }
  | { type: "RELEASED" }
  | { type: "SLOW"; attemptId: number }
  | { type: "ACKNOWLEDGE" };
